import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import type { BuildLead } from '@/utils/api-pb';

interface LeadsPanelProps {
  leads: BuildLead[];
  isLoading: boolean;
  isBusy: boolean;
  onExport: () => void;
  onDelete: (leadId: string) => void;
}

function formatDate(value: string, locale: string): string {
  const date = new Date(value.replace(' ', 'T'));
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString(locale === 'en' ? 'en-GB' : 'ru-RU', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** Where the visitor came from, when the link carried utm marks. */
function formatSource(lead: BuildLead): string {
  const utm = lead.source ?? {};
  const parts = [utm.utm_source, utm.utm_campaign].filter(Boolean);
  if (parts.length) return parts.join(' · ');

  if (!lead.referrer) return '';
  try {
    return new URL(lead.referrer).hostname;
  } catch {
    return '';
  }
}

/**
 * Submissions from the published site.
 *
 * Flagged ones are shown alongside the rest rather than hidden: the anti-spam checks
 * are deliberately crude, and a real customer who tripped one is exactly the person
 * the owner cannot afford to lose silently.
 */
export function LeadsPanel({ leads, isLoading, isBusy, onExport, onDelete }: LeadsPanelProps) {
  const { t, i18n } = useTranslation('builder');

  if (isLoading) {
    return (
      <p className="font-segoe text-[14px] text-[color:var(--grey-text,#565858)]">…</p>
    );
  }

  if (!leads.length) {
    return (
      <p className="font-segoe text-[14px] text-[color:var(--grey-text,#565858)]">
        {t('leads.empty')}
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-[12px]">
      <div className="flex items-center justify-between gap-[8px]">
        <span className="font-segoe text-[13px] text-[color:var(--grey-text,#565858)]">
          {t('leads.count', { count: leads.filter((lead) => !lead.isSpam).length })}
        </span>
        <Button variant="brand-soft" size="brand-sm" disabled={isBusy} onClick={onExport}>
          {t('leads.export')}
        </Button>
      </div>

      <ul className="flex max-h-[420px] flex-col gap-[8px] overflow-y-auto">
        {leads.map((lead) => {
          const source = formatSource(lead);

          return (
            <li
              key={lead.id}
              className="flex flex-col gap-[6px] rounded-[16px] border border-[var(--grey-accent,#eeeeee)] px-[12px] py-[10px]"
            >
              <div className="flex items-start justify-between gap-[8px]">
                <span className="font-segoe text-[12px] text-[color:var(--grey-text,#969696)]">
                  {formatDate(lead.created, i18n.language)}
                  {source && ` · ${source}`}
                  {lead.isSpam && (
                    <span className="ml-[6px] rounded-[4px] bg-[var(--error-soft,#ffeded)] px-[6px] py-[1px] text-[color:var(--error-strong,#b3261e)]">
                      {t('leads.spam')}
                    </span>
                  )}
                </span>
                <Button
                  variant="brand-danger"
                  size="brand-sm"
                  disabled={isBusy}
                  onClick={() => onDelete(lead.id)}
                >
                  {t('leads.delete')}
                </Button>
              </div>

              <dl className="grid gap-[2px]">
                {Object.entries(lead.data).map(([field, value]) => (
                  <div key={field} className="flex gap-[6px]">
                    <dt className="shrink-0 font-segoe text-[13px] text-[color:var(--grey-text,#565858)]">
                      {t(`leads.field.${field}`, { defaultValue: field })}:
                    </dt>
                    {/* break-all: адреса и длинные ответы не должны рвать карточку */}
                    <dd className="break-all font-segoe text-[13px] text-[color:var(--black-text,#0d0d0d)]">
                      {value}
                    </dd>
                  </div>
                ))}
              </dl>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
